import axios from 'axios';
import { API_BASE_URL } from '../config/config';

// 스마트링 바이탈 조회. 권한 범위 파라미터를 보내지 않는다 —
// 서버가 토큰으로 "누가 묻는가"만 확인하고 접근 가능 여부는 매 요청 DB 에
// 다시 조회한다(src/server/vitals/access.js).

// yRange 는 세로축 고정 범위다.
//
// 왜 고정하는가: Chart.js 는 기본으로 그 사람 그 기간의 최소~최대에 축을 맞춘다.
// 그러면 SpO2 97~99 인 사람과 89~99 인 사람의 그래프가 똑같은 높이로 그려져
// 사람끼리도, 같은 사람의 다른 기간끼리도 눈으로 비교가 안 된다. 정상 범위 안의
// 미세한 흔들림이 큰 산맥처럼 보이는 문제도 같은 원인이다.
//
// 범위는 스마트링 클러스터 전체 사용자 분포(2026-08-31 확인)와 통상 생리 범위를
// 함께 보고 잡았다. 괄호는 실측 p1~p99 다.
//   심박   40~140  (53~113,  실측 39~121)
//   HRV    20~100  (30~60)   — worker(heatRiskEngine)가 RMSSD 20ms 미만을 무효로 본다
//   스트레스 0~100  (20~65)   — 벤더 척도가 0~100
//   체온   35~39   (35.9~37.5, 실측 35.5~39.0) — 피부온이라 심부체온보다 낮다
//   SpO2   80~99   (96~99,   실측 89~99)
//
// ⚠️ 이 값은 hard clamp 가 아니라 suggestedMin/suggestedMax 로 쓴다.
//    범위를 벗어난 값이 있으면 축이 그만큼 넓어진다. 저산소나 고열처럼
//    가장 봐야 할 점이 축 밖으로 잘려 사라지면 안 된다.
export const SERIES_METRICS = [
    { key: 'heartRate', label: '심박', unit: 'bpm', valueKey: 'bpm', color: '#FF4136', yRange: { min: 40, max: 140 } },
    { key: 'hrv', label: 'HRV', unit: 'ms', valueKey: 'hrvMs', color: '#0074D9', yRange: { min: 20, max: 100 } },
    { key: 'stress', label: '스트레스', unit: '', valueKey: 'stressValue', color: '#FF851B', yRange: { min: 0, max: 100 } },
    { key: 'temperature', label: '체온', unit: '℃', valueKey: 'temperatureCelsius', color: '#B10DC9', yRange: { min: 35, max: 39 } },
    // SpO2 는 테이블에 시간별 min/max 만 있고 평균이 없다. 최솟값을 그린다 —
    // 저산소를 놓치지 않는 쪽이 안전하고, 없는 평균을 합성하지 않는다.
    { key: 'spo2', label: 'SpO2', unit: '%', valueKey: 'minSpo2', color: '#2ECC40', yRange: { min: 80, max: 99 } }
];

// 시계열 31일, 일일 요약 366일. 서버 상한과 같아야 400 을 미리 막을 수 있다.
export const MAX_SERIES_DAYS = 31;
export const MAX_DAILY_DAYS = 366;

// 로컬 시각 기준 yyyy-mm-dd. toISOString() 을 쓰면 UTC 로 넘어가며 하루 밀린다.
export const toDateInput = (date) => {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
};

export const daysAgo = (count) => {
    const date = new Date();
    date.setDate(date.getDate() - count);
    return toDateInput(date);
};

export const dayCount = (startDate, endDate) => Math.round(
    (Date.parse(`${endDate}T00:00:00Z`) - Date.parse(`${startDate}T00:00:00Z`)) / 86400000
) + 1;

// 서버가 주는 error.code 를 화면 문구로 옮긴다.
// 코드가 없으면 일반 오류로 처리한다 — 문구를 지어내지 않는다.
export const describeVitalsError = (error) => {
    const code = error?.response?.data?.error?.code;
    switch (code) {
        case 'VITALS_DISABLED':
            return { code, message: '스마트링 바이탈 조회 기능이 꺼져 있습니다. (SMARTRING_VITALS_ENABLED)' };
        case 'VITALS_UPSTREAM_UNAVAILABLE':
            return { code, message: '스마트링 데이터 서버에 연결할 수 없습니다. 잠시 후 다시 시도해 주세요.' };
        case 'VITALS_ACCESS_DENIED':
            return { code, message: '이 대상자의 바이탈을 조회할 권한이 없습니다.' };
        case 'EXPIRING_TOKEN_REQUIRED':
            return { code, message: '세션이 만료되었습니다. 다시 로그인해 주세요.' };
        case 'INVALID_PARAMETER':
            return {
                code,
                message: `조회 조건이 올바르지 않습니다. ${(error.response.data.error.details || []).join(' ')}`.trim()
            };
        default:
            return { code: code || null, message: '바이탈 데이터를 불러오지 못했습니다.' };
    }
};

const authHeaders = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

export const fetchVitalsSeries = async ({ token, appUserId, startDate, endDate, metrics, deviceId }) => {
    const params = { startDate, endDate };
    if (metrics && metrics.length > 0) params.metrics = metrics.join(',');
    if (deviceId) params.deviceId = deviceId;

    const response = await axios.get(
        `${API_BASE_URL}/api/patients/${appUserId}/vitals/series`,
        { ...authHeaders(token), params }
    );
    return response.data;
};

export const fetchVitalsDaily = async ({
    token, appUserId, startDate, endDate, deviceId, includeSleepSegments
}) => {
    const params = { startDate, endDate };
    if (deviceId) params.deviceId = deviceId;
    // 수면 카드(히프노그램)용. 밤마다 세그먼트 수십 행이 붙으므로
    // 시계열과 같은 상한(31일) 안에서만 요청하는 것을 권장한다.
    if (includeSleepSegments) params.includeSleepSegments = 'true';

    const response = await axios.get(
        `${API_BASE_URL}/api/patients/${appUserId}/vitals/daily`,
        { ...authHeaders(token), params }
    );
    return response.data;
};

// 최근 데이터가 있는 날(YYYY-MM-DD). 없으면 null. [최근] 버튼이 그 하루를 조회한다.
export const fetchVitalsLatestDate = async ({ token, appUserId, deviceId }) => {
    const params = {};
    if (deviceId) params.deviceId = deviceId;

    const response = await axios.get(
        `${API_BASE_URL}/api/patients/${appUserId}/vitals/latest`,
        { ...authHeaders(token), params }
    );
    return response.data?.latestDate || null;
};
